import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Hash, Sparkles, Play, Pause, Calendar } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { reduceNumber, getNumberMeaning } from '../domains/numerology/calculator';
import { getArchetype, getArchetypeWithCustomTerms, getLayeredArchetypes, getCulturalArchetypes } from '../domains/numerology/archetypes';
import NumerologicalTextReflection, { getNumerologyCharacterColor, getNumerologyCharacterValue } from './NumerologicalTextReflection';
import NumerologyColorLegend from './NumerologyColorLegend';

/**
 * Numbers Are A Story - Real-Time Numerology Panel
 * 
 * Expands upon the "numbers are a story" concept by tracing the current time
 * in real-time, showing numerology, archetypes, and arrangements for:
 * - Month, Day, Year, Hour, Minute, Second
 */

interface TimeNumerology {
  label: string;
  rawValue: number;
  reducedValue: number;
  archetype: ReturnType<typeof getArchetype>;
  layeredArchetypes: ReturnType<typeof getLayeredArchetypes>;
  meaning: ReturnType<typeof getNumberMeaning>;
}

const NumbersAreAStoryPanel: React.FC = () => {
  const { colors } = useTheme();
  const [isLiveMode, setIsLiveMode] = useState(false);
  const [customDateTime, setCustomDateTime] = useState<string>('');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [storyText, setStoryText] = useState('');
  const [timeData, setTimeData] = useState<TimeNumerology[]>([]);
  const [refreshKey, setRefreshKey] = useState(0); // Force refresh when custom terms change
  const pageLoadTime = useRef(new Date());

  useEffect(() => {
    const savedText = localStorage.getItem('fr00t_numbers_story_text');
    if (savedText) {
      setStoryText(savedText);
      return;
    }

    setStoryText('PAIR - PAIR PRINCIPLE • PARTNERSHIP • TWO LINES • TWO PRINCIPLES');
  }, []);

  useEffect(() => {
    localStorage.setItem('fr00t_numbers_story_text', storyText);
  }, [storyText]);

  // Listen for custom terms changes in localStorage
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'fr00t_custom_terms') {
        // Force refresh by updating refreshKey
        setRefreshKey(prev => prev + 1);
      }
    };

    // Listen for storage events (from other tabs/windows)
    window.addEventListener('storage', handleStorageChange);

    // Also listen for custom events we can dispatch when terms change in same tab
    const handleCustomTermsChange = () => {
      setRefreshKey(prev => prev + 1);
    };
    window.addEventListener('customTermsUpdated', handleCustomTermsChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('customTermsUpdated', handleCustomTermsChange);
    };
  }, []);

  // Update time every second only if live mode is on
  useEffect(() => {
    if (!isLiveMode) return;

    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(interval);
  }, [isLiveMode]);

  // Handle custom date/time input
  const handleDateTimeChange = (dateTimeString: string) => {
    setCustomDateTime(dateTimeString);
    if (dateTimeString) {
      const customDate = new Date(dateTimeString);
      if (!isNaN(customDate.getTime())) {
        setCurrentTime(customDate);
        setIsLiveMode(false); // Disable live mode when using custom date
      }
    }
  };

  // Calculate numerology for all time components
  useEffect(() => {
    const timeToUse = customDateTime ? new Date(customDateTime) : (isLiveMode ? currentTime : pageLoadTime.current);
    const now = timeToUse;
    const month = now.getMonth() + 1;
    const day = now.getDate();
    const year = now.getFullYear();
    const hour = now.getHours();
    const minute = now.getMinutes();
    const second = now.getSeconds();

    const calculateTimeNumerology = (label: string, value: number): TimeNumerology => {
      const reduced = reduceNumber(value);
      const archetype = getArchetypeWithCustomTerms(reduced);
      const layered = getLayeredArchetypes(value);
      const meaning = getNumberMeaning(value);

      return {
        label,
        rawValue: value,
        reducedValue: reduced,
        archetype,
        layeredArchetypes: layered,
        meaning
      };
    };

    const data: TimeNumerology[] = [
      calculateTimeNumerology('Year', year),
      calculateTimeNumerology('Month', month),
      calculateTimeNumerology('Day', day),
      calculateTimeNumerology('Hour', hour),
      calculateTimeNumerology('Minute', minute),
      calculateTimeNumerology('Second', second)
    ];

    setTimeData(data);
  }, [currentTime, isLiveMode, customDateTime, refreshKey]);

  // Calculate combined time numerology
  const combinedValue = timeData.length > 0 
    ? timeData.reduce((sum, item) => sum + item.reducedValue, 0)
    : 0;
  const combinedReduced = combinedValue > 0 ? reduceNumber(combinedValue) : 0;
  const combinedArchetype = combinedReduced > 0 ? getArchetypeWithCustomTerms(combinedReduced) : null;
  const combinedLayered = combinedValue > 0 ? getLayeredArchetypes(combinedValue) : { archetypes: [], combinations: [] };

  // Build a gradient for the combined number from the individual time-part colors
  const combinedGradientColors = timeData
    .map((t) => t.archetype?.color)
    .filter((color, index, arr): color is string => Boolean(color) && arr.indexOf(color) === index);

  const combinedGradient =
    combinedGradientColors.length > 1
      ? `linear-gradient(90deg, ${combinedGradientColors.join(', ')})`
      : undefined;

  // Format time display
  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', { 
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Get the datetime string for input field
  const getDateTimeString = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const displayTime = customDateTime ? new Date(customDateTime) : (isLiveMode ? currentTime : pageLoadTime.current);

  const renderNumerologicalPhrase = (phrase: string, neutralColor = '#94a3b8') => {
    return phrase.split('').map((char, idx) => {
      const value = getNumerologyCharacterValue(char);
      return (
        <span
          key={`${char}-${idx}`}
          style={{ color: value === null ? neutralColor : getNumerologyCharacterColor(char) }}
          title={value === null ? undefined : `${char.toUpperCase()} -> ${reduceNumber(value)}`}
        >
          {char}
        </span>
      );
    });
  };

  // Render a number or numeric string with each digit colored by its own digit archetype
  const renderColoredNumber = (value: number | string, fallbackColor: string) => {
    const str = String(value);
    return Array.from(str).map((char, index) => {
       if (/\d/.test(char)) {
         const digit = parseInt(char, 10);
         // Use the full number archetype to get a stable color for this digit
         const digitNumberArchetype = getArchetypeWithCustomTerms(digit);
         const digitColor = digitNumberArchetype?.color || fallbackColor;
        return (
          <span key={index} style={{ color: digitColor }}>
            {char}
          </span>
        );
      }

      // Non-digit characters (like separators) get a neutral gray
      return (
        <span key={index} style={{ color: colors.gray }}>
          {char}
        </span>
      );
    });
  };

  // Generate a comprehensible story sentence for each time component
  const generateStorySentence = (item: TimeNumerology): string => {
    const { label, rawValue, reducedValue, archetype } = item;
    const reductionText = rawValue !== reducedValue 
      ? `reduces to ${reducedValue}` 
      : `is ${reducedValue}`;
    
    // Create context-appropriate action verbs
    const contextActions: { [key: string]: string } = {
      'Year': 'carries',
      'Month': 'brings',
      'Day': 'sets',
      'Hour': 'influences',
      'Minute': 'shapes',
      'Second': 'pulses'
    };

    const action = contextActions[label] || 'embodies';
    const archetypeTitle = archetype?.title || 'Unknown';
    const essence = archetype?.essence || '';

    // Build a natural sentence
    let sentence = `The ${label} ${rawValue} ${reductionText}, ${action} the energy of ${archetypeTitle}`;
    
    // Add a brief essence snippet if available and concise
    if (essence) {
      // Take first sentence or first 80 characters, whichever is shorter
      const essenceSnippet = essence.split('.')[0].trim();
      if (essenceSnippet.length > 0 && essenceSnippet.length < 80) {
        sentence += `, ${essenceSnippet.toLowerCase()}`;
      }
    }
    
    return sentence + '.';
  };

  return (
    <div style={{
      padding: '1.5rem',
      display: 'flex',
      flexDirection: 'column',
      gap: '1.5rem',
      height: '100%',
      overflowY: 'auto'
    }}>
      <style>{`
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
            transform: scale(1);
            box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7);
          }
          50% {
            opacity: 0.8;
            transform: scale(1.1);
            box-shadow: 0 0 0 4px rgba(16, 185, 129, 0);
          }
        }
      `}</style>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        marginBottom: '0.5rem'
      }}>
        <Sparkles size={20} color={colors.purple} />
        <h2 style={{
          fontSize: '1.25rem',
          fontWeight: '600',
          color: colors.light,
          margin: 0
        }}>
          Numbers Are A Story
        </h2>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          {/* Date/Time Input */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.5rem',
            background: 'rgba(15, 23, 42, 0.5)',
            borderRadius: '6px',
            border: `1px solid ${colors.purpleAlpha(0.2)}`
          }}>
            <Calendar size={14} color={colors.gray} />
            <input
              type="datetime-local"
              name="numbers-story-datetime"
              aria-label="Custom date and time for numerology view"
              value={customDateTime || getDateTimeString(displayTime)}
              onChange={(e) => handleDateTimeChange(e.target.value)}
              style={{
                background: 'transparent',
                border: 'none',
                color: colors.light,
                fontSize: '0.75rem',
                fontFamily: 'monospace',
                outline: 'none',
                width: '160px'
              }}
            />
          </div>
          {/* Live Mode Toggle */}
          <motion.button
            onClick={() => {
              setIsLiveMode(!isLiveMode);
              if (!isLiveMode) {
                setCustomDateTime(''); // Clear custom date when enabling live mode
                setCurrentTime(new Date());
              }
            }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.5rem 0.75rem',
              background: isLiveMode 
                ? colors.emeraldAlpha(0.2) 
                : 'rgba(15, 23, 42, 0.5)',
              border: `1px solid ${isLiveMode ? colors.emerald : colors.purpleAlpha(0.3)}`,
              borderRadius: '6px',
              color: isLiveMode ? colors.emerald : colors.gray,
              cursor: 'pointer',
              fontSize: '0.75rem',
              fontWeight: '600',
              transition: 'all 0.2s'
            }}
          >
            {isLiveMode ? <Pause size={14} /> : <Play size={14} />}
            {isLiveMode ? 'Live' : 'Static'}
          </motion.button>
        </div>
      </div>

      {/* Current Time Display */}
      <motion.div
        key={displayTime.getTime()}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
        style={{
          padding: '1rem',
          background: '#1e293b', // Very dark slate gray
          border: `2px solid ${isLiveMode ? colors.emerald : colors.purple}`,
          borderRadius: '8px',
          textAlign: 'center',
          position: 'relative',
          boxShadow: `0 2px 8px rgba(0, 0, 0, 0.4)`
        }}
      >
        {isLiveMode && (
          <div style={{
            position: 'absolute',
            top: '0.5rem',
            right: '0.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.25rem',
            padding: '0.25rem 0.5rem',
            background: colors.emeraldAlpha(0.2),
            borderRadius: '4px',
            border: `1px solid ${colors.emeraldAlpha(0.3)}`
          }}>
            <div style={{
              width: '6px',
              height: '6px',
              borderRadius: '50%',
              background: colors.emerald,
              animation: 'pulse 2s ease-in-out infinite',
              boxShadow: `0 0 0 0 ${colors.emeraldAlpha(0.7)}`
            }} />
            <span style={{
              fontSize: '0.65rem',
              color: colors.emerald,
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Live
            </span>
          </div>
        )}
        <div style={{
          fontSize: '2rem',
          fontWeight: '700',
          fontFamily: 'monospace',
          letterSpacing: '2px',
          marginBottom: '0.25rem'
        }}>
          {renderColoredNumber(
            formatTime(displayTime),
            combinedArchetype?.color || colors.purple
          )}
        </div>
        <div style={{
          fontSize: '0.875rem',
          color: '#cbd5e1', // Light gray for contrast on dark
          fontFamily: 'monospace',
          fontWeight: '500'
        }}>
          {formatDate(displayTime)}
        </div>
        {customDateTime && !isLiveMode && (
          <div style={{
            fontSize: '0.7rem',
            color: '#94a3b8', // Medium-light gray for contrast
            fontStyle: 'italic',
            marginTop: '0.5rem'
          }}>
            Custom Date/Time
          </div>
        )}
        {!customDateTime && !isLiveMode && (
          <div style={{
            fontSize: '0.7rem',
            color: '#94a3b8', // Medium-light gray for contrast
            fontStyle: 'italic',
            marginTop: '0.5rem'
          }}>
            Page Load Time
          </div>
        )}
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
        style={{
          padding: '1rem',
          background: '#1e293b',
          border: `2px solid ${colors.purple}`,
          borderRadius: '10px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.4)'
        }}
      >
        <div style={{
          fontSize: '0.8rem',
          color: '#cbd5e1',
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          fontWeight: 700,
          marginBottom: '0.6rem'
        }}>
          Word-Numeric Story Field
        </div>

        <textarea
          value={storyText}
          onChange={(event) => setStoryText(event.target.value)}
          placeholder="Write words, symbols, and lines. Use new lines for phases and • to create subsection arcs."
          style={{
            width: '100%',
            minHeight: '120px',
            resize: 'vertical',
            borderRadius: '8px',
            padding: '0.75rem',
            border: `1px solid ${colors.purpleAlpha(0.35)}`,
            background: '#0f172a',
            color: '#e2e8f0',
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            lineHeight: 1.5,
            fontSize: '0.85rem'
          }}
        />

        <NumerologicalTextReflection text={storyText} />

        <div style={{ marginTop: '0.8rem' }}>
          <NumerologyColorLegend compact title="Story Color Legend" />
        </div>
      </motion.div>

      {/* Time Components Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
        gap: '1rem'
      }}>
        {timeData.map((item, index) => (
          <motion.div
            key={`${item.label}-${item.rawValue}`}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
            style={{
              padding: '1rem',
              background: '#1e293b', // Very dark slate gray
              border: `2px solid ${item.archetype?.color || colors.purple}`,
              borderRadius: '8px',
              boxShadow: '0 2px 6px rgba(0, 0, 0, 0.4)',
              display: 'flex',
              flexDirection: 'column',
              gap: '0.75rem'
            }}
          >
            {/* Label and Values */}
            <div>
              <div style={{
                fontSize: '0.75rem',
                color: '#cbd5e1', // Light gray for contrast on dark
                textTransform: 'uppercase',
                letterSpacing: '1px',
                marginBottom: '0.25rem',
                fontWeight: '600'
              }}>
                {item.label}
              </div>
              <div style={{
                display: 'flex',
                alignItems: 'baseline',
                gap: '0.5rem'
              }}>
                <span style={{
                  fontSize: '1.5rem',
                  fontWeight: '700',
                  fontFamily: 'monospace'
                }}>
                  {renderColoredNumber(
                    item.rawValue,
                    item.archetype.color || colors.light
                  )}
                </span>
                {item.rawValue !== item.reducedValue && (
                  <>
                    <span style={{ color: '#64748b' }}>→</span>
                    <span style={{
                      fontSize: '1.25rem',
                      fontWeight: '600',
                      fontFamily: 'monospace'
                    }}>
                      {renderColoredNumber(
                        item.reducedValue,
                        item.archetype.color || colors.purple
                      )}
                    </span>
                  </>
                )}
              </div>
            </div>

            {/* Archetype Title */}
            <div>
              <div style={{
                fontSize: '0.875rem',
                fontWeight: '600',
                color: item.archetype.color || colors.purple,
                marginBottom: '0.25rem'
              }}>
                {renderNumerologicalPhrase(item.archetype.title, '#cbd5e1')}
              </div>
              <div style={{
                fontSize: '0.75rem',
                color: '#e2e8f0', // Light slate for contrast on dark
                lineHeight: '1.4'
              }}>
                {renderNumerologicalPhrase(item.archetype.essence, '#cbd5e1')}
              </div>
            </div>

            {/* Core Archetypes */}
            {item.layeredArchetypes.archetypes.length > 0 && (
              <div>
                <div style={{
                  fontSize: '0.7rem',
                  color: '#cbd5e1', // Light gray for contrast on dark
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: '0.5rem',
                  fontWeight: '600'
                }}>
                  Core Archetypes
                </div>
                <div style={{
                  fontSize: '0.75rem',
                  lineHeight: '1.6',
                  fontStyle: 'italic'
                }}>
                  {item.layeredArchetypes.combinations.slice(0, 2).map((combo, i) => {
                    const terms = combo.terms || [];
                    const termDigits = combo.termDigits || {};
                    const arrangement = combo.arrangement;
                    
                    // Parse the arrangement and color each term individually
                    // Split by common separators (preserving them) and color each part
                    const segments = arrangement.split(/( ▪ | OF | WITH | AND )/g);
                    
                    return (
                      <div key={i} style={{ marginBottom: '0.25rem' }}>
                        {segments.map((segment, segIndex) => {
                          const trimmedSegment = segment.trim();
                          // Check if this segment is one of our terms
                          const isTerm = terms.some(term => term === trimmedSegment);
                          
                          if (isTerm) {
                            const digit = termDigits[trimmedSegment] || item.reducedValue;
                            const digitArchetype = getArchetypeWithCustomTerms(digit);
                            const termColor = digitArchetype?.color || colors.light;
                            return (
                              <span key={segIndex} style={{ color: termColor }}>
                                {renderNumerologicalPhrase(segment, '#94a3b8')}
                              </span>
                            );
                          } else {
                            // This is a separator/connector - render in dark gray
                            return (
                              <span key={segIndex} style={{ color: '#64748b' }}>
                                {renderNumerologicalPhrase(segment, '#64748b')}
                              </span>
                            );
                          }
                        })}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Keywords */}
            <div>
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '0.25rem'
              }}>
                {item.archetype.keywords.slice(0, 3).map((keyword, i) => (
                  <span
                    key={i}
                    style={{
                      fontSize: '0.65rem',
                      padding: '0.25rem 0.5rem',
                      // Tint keyword chips with the number's archetype color
                      background: item.archetype.color
                        ? `${item.archetype.color}33` // light alpha
                        : colors.purpleAlpha(0.2),
                      border: item.archetype.color
                        ? `1px solid ${item.archetype.color}66`
                        : `1px solid ${colors.purpleAlpha(0.3)}`,
                      borderRadius: '4px',
                      color: item.archetype.color || colors.purple,
                      fontFamily: 'monospace'
                    }}
                  >
                    {keyword}
                  </span>
                ))}
              </div>
            </div>

            {/* Cultural Archetypes Preview */}
            {getCulturalArchetypes(item.reducedValue).length > 0 && (
              <div style={{
                marginTop: '0.5rem',
                paddingTop: '0.5rem',
                borderTop: `1px solid ${item.archetype?.color || colors.purple}40`
              }}>
                <div style={{
                  fontSize: '0.65rem',
                  color: '#cbd5e1', // Light gray for contrast on dark
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: '0.5rem',
                  fontWeight: '600'
                }}>
                  Cultural Meanings ({getCulturalArchetypes(item.reducedValue).length})
                </div>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.4rem',
                  maxHeight: '120px',
                  overflowY: 'auto',
                  paddingRight: '0.25rem'
                }}>
                  {getCulturalArchetypes(item.reducedValue).slice(0, 3).map((cultural, cIdx) => (
                    <div
                      key={cIdx}
                      style={{
                        padding: '0.5rem',
                        background: '#0f172a', // Very dark slate background
                        border: `1px solid ${item.archetype?.color || colors.purple}80`,
                        borderRadius: '4px'
                      }}
                    >
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: '0.25rem'
                      }}>
                        <span style={{
                          fontSize: '0.7rem',
                          fontWeight: '600',
                          color: '#f1f5f9' // Light text for contrast on dark
                        }}>
                          {cultural.tradition}
                        </span>
                        {cultural.luckyStatus && (
                          <span style={{
                            fontSize: '0.6rem',
                            padding: '0.1rem 0.3rem',
                            background: cultural.luckyStatus === 'lucky' 
                              ? colors.emeraldAlpha(0.2) 
                              : cultural.luckyStatus === 'sacred'
                              ? colors.purpleAlpha(0.2)
                              : cultural.luckyStatus === 'unlucky'
                              ? colors.redAlpha(0.2)
                              : `rgba(100, 116, 139, 0.2)`,
                            borderRadius: '3px',
                            color: cultural.luckyStatus === 'lucky' 
                              ? colors.emerald 
                              : cultural.luckyStatus === 'sacred'
                              ? colors.purple
                              : cultural.luckyStatus === 'unlucky'
                              ? colors.red
                              : colors.gray,
                            textTransform: 'uppercase',
                            fontWeight: '600'
                          }}>
                            {cultural.luckyStatus}
                          </span>
                        )}
                      </div>
                      <p style={{
                        fontSize: '0.7rem',
                        color: '#e2e8f0', // Light slate for contrast on dark
                        lineHeight: '1.4',
                        margin: 0
                      }}>
                        {renderNumerologicalPhrase(cultural.meaning, '#cbd5e1')}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        ))}
      </div>

      {/* Story Summary Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        style={{
          padding: '1.5rem',
          background: '#1e293b',
          border: `2px solid ${colors.purple}`,
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)'
        }}
      >
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          marginBottom: '1rem'
        }}>
          <Sparkles size={18} color={colors.purple} />
          <h3 style={{
            fontSize: '1rem',
            fontWeight: '600',
            color: '#f1f5f9',
            margin: 0
          }}>
            The Story of This Moment
          </h3>
        </div>

        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem'
        }}>
          {timeData.map((item, index) => {
            const storySentence = generateStorySentence(item);
            return (
              <motion.div
                key={`story-${item.label}-${item.rawValue}`}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.25 + index * 0.05 }}
                style={{
                  padding: '1rem',
                  background: '#0f172a',
                  border: `1px solid ${item.archetype?.color || colors.purple}80`,
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '0.75rem'
                }}
              >
                <div style={{
                  minWidth: '60px',
                  fontSize: '0.75rem',
                  fontWeight: '600',
                  color: item.archetype?.color || colors.purple,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  paddingTop: '0.125rem'
                }}>
                  {item.label}
                </div>
                <p style={{
                  fontSize: '0.875rem',
                  color: '#e2e8f0',
                  lineHeight: '1.6',
                  margin: 0,
                  flex: 1
                }}>
                  {renderNumerologicalPhrase(storySentence, '#cbd5e1')}
                </p>
              </motion.div>
            );
          })}
        </div>
      </motion.div>

      {/* Combined Time Numerology */}
      <div
        style={{
          height: '500px',
          display: 'flex',
          flexDirection: 'column',
          marginTop: '0.5rem'
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          style={{
            padding: '1.5rem',
            background: '#1e293b', // Very dark slate gray
            border: `2px solid ${colors.indigo}`,
            borderRadius: '12px',
            height: '100%',
            overflowY: 'auto',
            overflowX: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)'
          }}
        >
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          marginBottom: '1rem'
        }}>
          <Hash size={18} color={colors.indigo} />
          <h3 style={{
            fontSize: '1rem',
            fontWeight: '600',
            color: '#f1f5f9', // Light slate for contrast on dark
            margin: 0
          }}>
            Combined Time Numerology
          </h3>
        </div>

        {combinedArchetype && (
          <>
            <div style={{
              display: 'flex',
              alignItems: 'baseline',
              gap: '0.75rem',
              marginBottom: '1rem'
            }}>
              <span
                style={{
                  fontSize: '2rem',
                  fontWeight: '700',
                  fontFamily: 'monospace',
                  background: combinedGradient,
                  WebkitBackgroundClip: combinedGradient ? 'text' : undefined,
                  color: combinedGradient
                    ? 'transparent'
                    : combinedArchetype?.color || colors.indigo
                }}
              >
                {combinedReduced}
              </span>
              <span style={{
                fontSize: '1rem',
                color: '#64748b' // Medium gray for contrast
              }}>
                ({timeData.map(t => t.reducedValue).join(' + ')} = {combinedValue} → {combinedReduced})
              </span>
            </div>

            <div style={{
              fontSize: '1.125rem',
              fontWeight: '600',
              color: combinedArchetype.color || colors.indigo,
              marginBottom: '0.5rem'
            }}>
              {renderNumerologicalPhrase(combinedArchetype.title, '#cbd5e1')}
            </div>

            <div style={{
              fontSize: '0.875rem',
              color: '#e2e8f0', // Light slate for contrast on dark
              lineHeight: '1.6',
              marginBottom: '1rem'
            }}>
              {renderNumerologicalPhrase(combinedArchetype.essence, '#cbd5e1')}
            </div>
          </>
        )}

        {/* All Arrangements */}
        {combinedLayered.combinations.length > 0 && (
          <div>
            <div style={{
              fontSize: '0.75rem',
              color: '#cbd5e1', // Light gray for contrast on dark
              textTransform: 'uppercase',
              letterSpacing: '1px',
              marginBottom: '0.75rem',
              fontWeight: '600'
            }}>
              All Arrangements
            </div>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.5rem'
            }}>
              {combinedLayered.combinations.slice(0, 5).map((combo, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4 + i * 0.05 }}
                  style={{
                    padding: '0.75rem',
                    background: '#0f172a', // Very dark slate background
                    border: `1px solid ${colors.indigo}80`,
                    borderRadius: '6px',
                    fontSize: '0.8rem',
                    color: '#f1f5f9', // Light text for contrast on dark
                    fontStyle: 'italic',
                    lineHeight: '1.5'
                  }}
                >
                  {renderNumerologicalPhrase(combo.arrangement, '#cbd5e1')}
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* Strengths & Challenges */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem',
          marginTop: '1rem'
        }}>
          <div>
            <div style={{
              fontSize: '0.75rem',
              color: '#cbd5e1', // Light gray for contrast on dark
              textTransform: 'uppercase',
              letterSpacing: '1px',
              marginBottom: '0.5rem',
              fontWeight: '600'
            }}>
              Strengths
            </div>
            <ul style={{
              margin: 0,
              paddingLeft: '1.25rem',
              fontSize: '0.8rem',
              color: '#e2e8f0', // Light slate for contrast on dark
              lineHeight: '1.6'
            }}>
              {combinedArchetype?.strengths.slice(0, 3).map((strength, i) => (
                <li key={i}>{renderNumerologicalPhrase(strength, '#cbd5e1')}</li>
              ))}
            </ul>
          </div>

          <div>
            <div style={{
              fontSize: '0.75rem',
              color: '#cbd5e1', // Light gray for contrast on dark
              textTransform: 'uppercase',
              letterSpacing: '1px',
              marginBottom: '0.5rem',
              fontWeight: '600'
            }}>
              Challenges
            </div>
            <ul style={{
              margin: 0,
              paddingLeft: '1.25rem',
              fontSize: '0.8rem',
              color: '#e2e8f0', // Light slate for contrast on dark
              lineHeight: '1.6'
            }}>
              {combinedArchetype?.challenges.slice(0, 3).map((challenge, i) => (
                <li key={i}>{renderNumerologicalPhrase(challenge, '#cbd5e1')}</li>
              ))}
            </ul>
          </div>
        </div>

        {/* Sacred Connections */}
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '0.75rem',
          marginTop: '1rem',
          paddingTop: '1rem',
          borderTop: `1px solid ${colors.indigoAlpha(0.2)}`
        }}>
          <div>
            <span style={{ fontSize: '0.7rem', color: colors.gray, marginRight: '0.5rem' }}>Color:</span>
            <span style={{
              fontSize: '0.8rem',
              color: combinedArchetype?.color || colors.indigo,
              fontWeight: '600'
            }}>
              {combinedArchetype?.color || 'N/A'}
            </span>
          </div>
          <div>
            <span style={{ fontSize: '0.7rem', color: colors.gray, marginRight: '0.5rem' }}>Element:</span>
            <span style={{
              fontSize: '0.8rem',
              color: colors.light,
              fontWeight: '600'
            }}>
              {combinedArchetype?.element || 'N/A'}
            </span>
          </div>
          {combinedArchetype?.frequency && (
            <div>
              <span style={{ fontSize: '0.7rem', color: colors.gray, marginRight: '0.5rem' }}>Frequency:</span>
              <span style={{
                fontSize: '0.8rem',
                color: colors.light,
                fontWeight: '600',
                fontFamily: 'monospace'
              }}>
                {combinedArchetype.frequency} Hz
              </span>
            </div>
          )}
        </div>

        {/* Cultural Archetype Mappings */}
        {combinedReduced > 0 && getCulturalArchetypes(combinedReduced).length > 0 && (
          <div style={{
            marginTop: '1.5rem',
            paddingTop: '1.5rem',
            borderTop: `1px solid ${colors.indigoAlpha(0.2)}`
          }}>
            <div style={{
              fontSize: '0.75rem',
              color: colors.gray,
              textTransform: 'uppercase',
              letterSpacing: '1px',
              marginBottom: '1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <span>🌍</span>
              <span>Cultural Archetype Mappings</span>
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
              gap: '0.75rem',
              maxHeight: '300px',
              overflowY: 'auto',
              overflowX: 'hidden',
              paddingRight: '0.5rem'
            }}>
              {getCulturalArchetypes(combinedReduced).map((cultural, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 + idx * 0.03 }}
                  style={{
                    padding: '0.875rem',
                    background: '#0f172a', // Very dark slate background
                    border: `1px solid ${colors.indigo}80`,
                    borderRadius: '6px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.5rem'
                  }}
                >
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '0.25rem'
                  }}>
                    <span style={{
                      fontSize: '0.8rem',
                      fontWeight: '600',
                      color: colors.light
                    }}>
                      {cultural.tradition}
                    </span>
                    {cultural.luckyStatus && (
                      <span style={{
                        fontSize: '0.65rem',
                        padding: '0.15rem 0.4rem',
                        background: cultural.luckyStatus === 'lucky' 
                          ? colors.emeraldAlpha(0.2) 
                          : cultural.luckyStatus === 'sacred'
                          ? colors.purpleAlpha(0.2)
                          : cultural.luckyStatus === 'unlucky'
                          ? colors.redAlpha(0.2)
                          : `rgba(100, 116, 139, 0.2)`,
                        border: `1px solid ${cultural.luckyStatus === 'lucky' 
                          ? colors.emeraldAlpha(0.3) 
                          : cultural.luckyStatus === 'sacred'
                          ? colors.purpleAlpha(0.3)
                          : cultural.luckyStatus === 'unlucky'
                          ? colors.redAlpha(0.3)
                          : `rgba(100, 116, 139, 0.3)`}`,
                        borderRadius: '4px',
                        color: cultural.luckyStatus === 'lucky' 
                          ? colors.emerald 
                          : cultural.luckyStatus === 'sacred'
                          ? colors.purple
                          : cultural.luckyStatus === 'unlucky'
                          ? colors.red
                          : colors.gray,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        fontWeight: '600'
                      }}>
                        {cultural.luckyStatus}
                      </span>
                    )}
                  </div>
                  <p style={{
                    fontSize: '0.75rem',
                    color: colors.light,
                    lineHeight: '1.5',
                    margin: 0
                  }}>
                    {renderNumerologicalPhrase(cultural.meaning, '#cbd5e1')}
                  </p>
                  {cultural.symbolism && cultural.symbolism.length > 0 && (
                    <div style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '0.25rem',
                      marginTop: '0.25rem'
                    }}>
                      {cultural.symbolism.slice(0, 4).map((symbol, sIdx) => (
                        <span
                          key={sIdx}
                          style={{
                            fontSize: '0.65rem',
                            padding: '0.15rem 0.35rem',
                            background: colors.indigoAlpha(0.15),
                            border: `1px solid ${colors.indigoAlpha(0.25)}`,
                            borderRadius: '3px',
                            color: colors.indigo,
                            fontFamily: 'monospace'
                          }}
                        >
                          {symbol}
                        </span>
                      ))}
                    </div>
                  )}
                  {cultural.notes && (
                    <p style={{
                      fontSize: '0.7rem',
                      color: colors.gray,
                      fontStyle: 'italic',
                      lineHeight: '1.4',
                      margin: 0,
                      marginTop: '0.25rem'
                    }}>
                      {renderNumerologicalPhrase(cultural.notes, '#94a3b8')}
                    </p>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        )}
        </motion.div>
      </div>

      {/* Footer Note */}
      <div style={{
        fontSize: '0.7rem',
        color: colors.gray,
        textAlign: 'center',
        fontStyle: 'italic',
        paddingTop: '0.5rem',
        borderTop: `1px solid ${colors.purpleAlpha(0.1)}`
      }}>
        {renderNumerologicalPhrase('Each moment tells a story through numbers. The archetypes reveal the deeper meaning.', '#94a3b8')}
      </div>
    </div>
  );
};

export default NumbersAreAStoryPanel;

